import { apiRequest } from "@/lib/api-request";
import {
  isUuid,
  normalizeRoleplayCode,
  parseCatalogue,
  parseGameResponse,
  ROLEPLAY_CONTRACT,
  RoleplayError,
  type Catalogue,
  type GameResponse,
} from "./contract";

// The roleplay game's JSON routes through the BFF (B1 to B4 and B8 of play contract 3). Each answer is validated
// before it is returned; each failure is a RoleplayError carrying the contract's code, never the server's text.
// A game id reaches a path only once it is a UUID.

function gamePath(gameId: string, suffix = ""): string {
  if (!isUuid(gameId)) throw new RoleplayError("ROLEPLAY_INVALID_INPUT");
  return `ai/roleplay/games/${encodeURIComponent(gameId)}${suffix}`;
}

async function failure(response: Response): Promise<RoleplayError> {
  let code: unknown;
  try {
    code = ((await response.json()) as { errors?: { extensions?: { code?: unknown } }[] } | null)?.errors?.[0]?.extensions?.code;
  } catch {
    code = undefined;
  }
  return new RoleplayError(normalizeRoleplayCode(code), response.status);
}

async function request(path: string, init: { method?: "GET" | "POST"; signal?: AbortSignal } = {}): Promise<unknown> {
  const post = init.method === "POST";
  let response: Response;
  try {
    ({ response } = await apiRequest(path, {
      method: init.method ?? "GET",
      signal: init.signal,
      csrf: post,
      headers: post ? { Accept: "application/json", "Content-Type": "application/json" } : { Accept: "application/json" },
      ...(post ? { body: JSON.stringify({ contract: ROLEPLAY_CONTRACT }) } : {}),
    }));
  } catch {
    throw new RoleplayError("UNKNOWN");
  }
  if (!response.ok) throw await failure(response);
  try {
    return await response.json();
  } catch {
    throw new RoleplayError("ROLEPLAY_UPDATE_NEEDED", response.status);
  }
}

/** B1: what may start and what the player has open. */
export async function fetchCatalogue(signal?: AbortSignal): Promise<Catalogue> {
  return parseCatalogue(await request("ai/roleplay/catalogue", { signal }));
}

// An answer about another game than the one asked for is not one the page may act on: it would show one game and
// send the next turn to another.
function ofGame(gameId: string, response: GameResponse): GameResponse {
  if (response.game.id.toLowerCase() !== gameId.toLowerCase()) throw new RoleplayError("UNKNOWN");
  return response;
}

/** B3: open a game; a scored game's story is created the first time. */
export async function openGame(gameId: string): Promise<GameResponse> {
  return ofGame(gameId, parseGameResponse(await request(gamePath(gameId, "/open"), { method: "POST" })));
}

/** B4: read a game; changes nothing. */
export async function readGame(gameId: string, signal?: AbortSignal): Promise<GameResponse> {
  return ofGame(gameId, parseGameResponse(await request(gamePath(gameId), { signal })));
}
